"use client";
import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getSuppliers } from "@/state/suppliers/supplier.slice";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Loader2, RefreshCcw, Undo2, ChevronLeft, ChevronRight, Search, X } from "lucide-react";
import { format } from "date-fns";
import { useToast } from "@/components/ui/use-toast";
import { useDebounce } from "@/hooks/useDebounce";
import { todayInTimezone, daysAgoInTimezone } from "@/utils/formatDate";
import { numberWithCommas } from "@/lib/formatCommas";

const API_URL = import.meta.env.VITE_API_URL;

export default function ReturnsPage() {
    const dispatch = useDispatch();
    const { toast } = useToast();
    const { suppliers } = useSelector((state) => state.supplier || { suppliers: [] });
    const currentUser = useSelector((state) => state.auth.user);
    const { business } = useSelector((state) => state.business || {});
    const tz = currentUser?.timezone || 'UTC';

    const businessDetails = business?.data || business;
    const currency = businessDetails?.currency || "GHS";

    const [returns, setReturns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [pagination, setPagination] = useState({});

    // Filters
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearch = useDebounce(searchTerm, 400);
    const [statusFilter, setStatusFilter] = useState("all");
    const [supplierFilter, setSupplierFilter] = useState("");
    const [startDate, setStartDate] = useState(() => todayInTimezone(tz));
    const [endDate, setEndDate] = useState(() => todayInTimezone(tz));

    const buildParams = useCallback(() => {
        const params = { page, limit };
        if (debouncedSearch) params.search = debouncedSearch;
        if (statusFilter !== "all") params.status = statusFilter;
        if (supplierFilter) params.supplier_id = supplierFilter;
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
        return params;
    }, [page, limit, debouncedSearch, statusFilter, supplierFilter, startDate, endDate]);

    const fetchReturns = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}purchases/returns`, {
                params: buildParams(),
                withCredentials: true,
            });
            setReturns(res.data?.data || []);
            setPagination(res.data?.pagination || {});
        } catch (err) {
            console.error("Failed to fetch returns:", err);
            toast({ variant: "destructive", title: "Error", description: "Failed to fetch returns" });
        } finally {
            setLoading(false);
        }
    }, [buildParams]);

    useEffect(() => {
        dispatch(getSuppliers());
    }, [dispatch]);

    useEffect(() => {
        fetchReturns();
    }, [fetchReturns]);

    const statusColor = (status) => {
        switch (status) {
            case "completed": return "bg-green-100 text-green-800";
            case "approved": return "bg-blue-100 text-blue-800";
            case "pending": return "bg-yellow-100 text-yellow-800";
            case "rejected": return "bg-red-100 text-red-800";
            default: return "bg-gray-100 text-gray-800";
        }
    };

    return (
        <div className="mx-auto p-4 space-y-4">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <div>
                    <h1 className="text-2xl font-bold">Purchase Returns</h1>
                    <p className="text-sm text-muted-foreground">Manage and track all purchase return transactions</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={fetchReturns} disabled={loading}>
                        <RefreshCcw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    </Button>
                    <a href="/dashboard/purchases/returns-all/returns/add">
                        <Button size="sm">
                            <Undo2 className="h-4 w-4 mr-2" />
                            Create Return
                        </Button>
                    </a>
                </div>
            </div>

            {/* Compact Filters Bar */}
            <Card className="p-3">
                <div className="flex flex-wrap gap-2 items-center">
                    {/* Search */}
                    <div className="flex items-center space-x-2 bg-muted/30 px-2 rounded-md border flex-1 min-w-[200px] max-w-[300px]">
                        <Search className="w-4 h-4 text-muted-foreground" />
                        <Input
                            placeholder="Search returns..."
                            value={searchTerm}
                            onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                            className="border-none shadow-none focus-visible:ring-0 h-8 bg-transparent"
                        />
                        {searchTerm && (
                            <button onClick={() => { setSearchTerm(""); setPage(1); }} className="text-muted-foreground hover:text-foreground">
                                <X className="w-3 h-3" />
                            </button>
                        )}
                    </div>

                    {/* Status Filter */}
                    <Select value={statusFilter} onValueChange={(val) => { setStatusFilter(val); setPage(1); }}>
                        <SelectTrigger className="w-[120px] h-8 text-xs">
                            <SelectValue placeholder="All Status" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Status</SelectItem>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="approved">Approved</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="rejected">Rejected</SelectItem>
                        </SelectContent>
                    </Select>

                    {/* Supplier Filter */}
                    {suppliers?.length > 0 && (
                        <Select value={supplierFilter} onValueChange={(val) => { setSupplierFilter(val === 'all' ? '' : val); setPage(1); }}>
                            <SelectTrigger className="w-[140px] h-8 text-xs">
                                <SelectValue placeholder="All Suppliers" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Suppliers</SelectItem>
                                {suppliers.map((supplier) => (
                                    <SelectItem key={supplier.id} value={supplier.id.toString()}>
                                        {supplier.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}

                    {/* Date Range */}
                    <div className="flex items-center gap-1 bg-muted/30 px-2 rounded-md border">
                        <Input
                            type="date"
                            value={startDate}
                            onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
                            className="w-[110px] h-7 bg-transparent border-none shadow-none focus-visible:ring-0 p-0 text-xs"
                        />
                        <span className="text-muted-foreground text-xs">to</span>
                        <Input
                            type="date"
                            value={endDate}
                            onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
                            className="w-[110px] h-7 bg-transparent border-none shadow-none focus-visible:ring-0 p-0 text-xs"
                        />
                    </div>

                    {/* Quick Date Filters */}
                    <div className="flex gap-1">
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => {
                            const today = todayInTimezone(tz);
                            setStartDate(today); setEndDate(today); setPage(1);
                        }}>Today</Button>
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => {
                            setStartDate(daysAgoInTimezone(7, tz)); setEndDate(todayInTimezone(tz)); setPage(1);
                        }}>Week</Button>
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => {
                            setStartDate(daysAgoInTimezone(30, tz)); setEndDate(todayInTimezone(tz)); setPage(1);
                        }}>Month</Button>
                    </div>
                </div>
            </Card>

            <Card className="p-0">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Return #</TableHead>
                            <TableHead>Purchase Ref</TableHead>
                            <TableHead>Supplier</TableHead>
                            <TableHead>Items</TableHead>
                            <TableHead>Total</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Processed By</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={8} className="h-24 text-center">
                                    <span className="flex items-center justify-center gap-2">
                                        <Loader2 className="animate-spin" /> Loading returns...
                                    </span>
                                </TableCell>
                            </TableRow>
                        ) : returns.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} className="h-24 text-center text-muted-foreground">
                                    <div className="flex flex-col items-center gap-2">
                                        <Undo2 className="h-8 w-8 opacity-50" />
                                        No returns found.
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : (
                            returns.map((ret) => (
                                <TableRow
                                    key={ret.id}
                                    className="cursor-pointer hover:bg-muted/50"
                                    onClick={() => window.location.href = `/dashboard/purchases/returns-all/returns/${ret.id}`}
                                >
                                    <TableCell className="text-xs whitespace-nowrap">{format(new Date(ret.createdAt), "dd MMM yyyy")}</TableCell>
                                    <TableCell className="font-mono text-xs">{ret.return_number || '-'}</TableCell>
                                    <TableCell className="text-xs">{ret.Purchase?.invoice_number || '-'}</TableCell>
                                    <TableCell className="text-xs">{ret.Purchase?.Supplier?.name || 'N/A'}</TableCell>
                                    <TableCell className="text-xs">{ret.items?.length || 0} items</TableCell>
                                    <TableCell className="font-medium text-xs">
                                        {currency} {numberWithCommas(parseFloat(ret.total || 0).toFixed(2))}
                                    </TableCell>
                                    <TableCell>
                                        <Badge className={statusColor(ret.status)} variant="outline">
                                            {ret.status?.toUpperCase()}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-xs">{ret.processedBy?.username || 'N/A'}</TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>

                {/* Pagination — only show when total items exceed current limit */}
                {(pagination?.total || 0) > limit && (
                    <div className="flex items-center justify-between px-4 py-3 border-t">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <span>Rows per page:</span>
                            <Select value={limit.toString()} onValueChange={(val) => { setLimit(parseInt(val)); setPage(1); }}>
                                <SelectTrigger className="w-[70px] h-8">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="10">10</SelectItem>
                                    <SelectItem value="20">20</SelectItem>
                                    <SelectItem value="50">50</SelectItem>
                                    <SelectItem value="100">100</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex items-center gap-4">
                            <span className="text-sm text-muted-foreground">
                                {pagination.total > 0
                                    ? `${(page - 1) * limit + 1}-${Math.min(page * limit, pagination.total)} of ${pagination.total}`
                                    : "0 items"}
                            </span>
                            <div className="flex gap-1">
                                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPage(p => p - 1)} disabled={page <= 1 || loading}>
                                    <ChevronLeft className="h-4 w-4" />
                                </Button>
                                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPage(p => p + 1)} disabled={page >= (pagination?.totalPages || 1) || loading}>
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </Card>
        </div>
    );
}
